/* eslint-disable @typescript-eslint/only-throw-error -- @tanstack/react-router requires throwing redirect objects */
import {
  redirect,
  Navigate,
  createRootRoute,
  createRouter,
  createRoute,
} from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import App from '../App';
import LoginPage from '../pages/login';
import LogoutPage from '../pages/logout';
import NotFoundPage from '../pages/not-found';
import { AuthProvider, getRedirectParams } from '../lib/services/auth.service';
import MagicLinkPage from '@/pages/login/link';
import HomePage from '@/pages/home';
import SplashPage from '@/pages/splash';
import ErrorPage from '@/pages/error';
import { getUserPolicy } from '@/lib/services/policy.service';
import { getPortalUrl } from '@/lib/helpers';

/*
 * Note: There is significant handling of the request based on the path which
 * happens in @/context/app/index.tsx, before the router even renders. Check
 * the useEffect hooks in that file for further notes.
 */

const rootRoute = createRootRoute({
  component: App,
});

const excludeAuth = () => {
  if (AuthProvider.user) {
    throw redirect({
      to: '/home',
      search: getRedirectParams(),
    });
  }
};

const requireAuth = () => {
  if (!AuthProvider.user) {
    throw redirect({
      to: '/login',
      search: getRedirectParams(),
    });
  }
};

export type SplashPageAction =
  | 'error'
  | 'block'
  | 'redirect'
  | 'downtime'
  | 'portal-access'
  | 'portal-redirect-error'
  | 'admin-access'
  | 'partner-access'
  | 'subscription-expired';

const isSplashPageAction = (value: string): value is SplashPageAction => {
  return [
    'error',
    'block',
    'redirect',
    'downtime',
    'portal-access',
    'portal-redirect-error',
    'admin-access',
    'partner-access',
    'subscription-expired',
  ].includes(value);
};

type SplashPageSearch = {
  action: SplashPageAction;
  destination?: string;
  traceId?: string;
  code?: string;
  redirectUrl?: string;
};

type LoginPageSearch = {
  redirect_to?: string;
  auth_type?: string;
};

const routes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: () => {
      return <Navigate to="/login" search={getRedirectParams()} />;
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: LoginPage,
    beforeLoad: excludeAuth,
    validateSearch: ({ redirect_to, auth_type }: Record<string, string>): LoginPageSearch => {
      return {
        redirect_to: redirect_to || '',
        auth_type: auth_type || '',
      };
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/login/link',
    component: MagicLinkPage,
    beforeLoad: excludeAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/logout',
    component: LogoutPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/home',
    component: HomePage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/splash',
    component: SplashPage,
    validateSearch: ({
      action,
      destination,
      request_id,
      trace_id,
      redirect_url,
      code,
    }: Record<string, unknown>): SplashPageSearch => {
      const compTraceId = trace_id || request_id || '';
      return {
        action: (action as SplashPageAction) || '',
        destination: destination ? DOMPurify.sanitize(String(destination)) : '',
        traceId: compTraceId ? DOMPurify.sanitize(String(compTraceId)) : '',
        code: code ? DOMPurify.sanitize(String(code)) : '',
        redirectUrl: redirect_url ? DOMPurify.sanitize(String(redirect_url)) : '',
      };
    },
    beforeLoad: ({ search }) => {
      if (!isSplashPageAction(search.action)) {
        throw redirect({ to: '/' });
      }
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/portal-redirect',
    component: HomePage,
    beforeLoad: async () => {
      if (!AuthProvider.user) {
        throw redirect({ to: '/', search: { redirect_to: '/portal-redirect' } });
      }

      const userPolicy = await getUserPolicy();
      const { blockAction, redirectUrl: configRedirectUrl } = userPolicy?.publicServices || {};

      if (blockAction === 'block') {
        throw redirect({ to: '/splash', search: { action: 'portal-redirect-error' } });
      }

      const redirectUrl =
        blockAction === 'redirect-portal' ? getPortalUrl(true) : configRedirectUrl;

      if (!userPolicy?.hasValidRedirect || !redirectUrl) {
        throw redirect({ to: '/splash', search: { action: 'portal-redirect-error' } });
      }

      window.location.href = redirectUrl;
      return new Promise(() => null);
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/error',
    component: ErrorPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '*',
    component: NotFoundPage,
    beforeLoad: requireAuth,
  }),
];

const routeTree = rootRoute.addChildren(routes);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default router;
